import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import CalendarScreen from './components/CalendarScreen';
import DashboardPage from './components/DashboardPage';
import SuccessPage from './components/SuccessPage';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import SupportPage from './components/SupportPage';
import LandingPage from './components/LandingPage';
import LoginModal from './components/LoginModal';
import ForgotPasswordPage from './components/ForgotPasswordPage';
import { auth } from './firebase';
import './App.css';
import MetricsDashboard from './components/MetricsDashboard';
import userJourneyTracker from './hooks/user-journey-tracker';
import visitorTracker from './hooks/VisitorTracker';
import DeleteAccountPage from './components/DeleteAccountPage';
import BlogIndex from './components/BlogIndex';
import BlogPost from './components/BlogPost';
import AppShell from './components/AppShell';
import { FinanceDataProvider } from './contexts/FinanceDataContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { SidebarProvider } from './contexts/SidebarContext';
import { ThemeProvider } from './contexts/ThemeContext';

// Stub pages for future phases
import BudgetsPage from './components/stubs/BudgetsPage';
import AccountsPage from './components/stubs/AccountsPage';
import InsightsPage from './components/stubs/InsightsPage';
import DebtPage from './components/stubs/DebtPage';
import SavingsPage from './components/stubs/SavingsPage';
import LedgerPage from './components/stubs/LedgerPage';
import SettingsPage from './components/stubs/SettingsPage';

// Redirect authenticated users from landing page to /app
const LandingRedirect = ({ user }) => {
  const navigate = useNavigate();
  useEffect(() => {
    if (user) navigate('/app', { replace: true });
  }, [user, navigate]);
  return user ? null : <LandingPage />;
};

// This wrapper component will handle the login modal display logic
const AppContent = ({ user, loading, authTimestamp, onAuthSuccess }) => {
  const location = useLocation();
  const [showLoginModal, setShowLoginModal] = useState(false);

  // Define public routes that don't require login
  const publicRoutes = ['/', '/privacy-policy', '/terms-of-service', '/forgot-password', '/support', '/blog'];

  // Define routes that handle their own authentication (don't need the global login modal)
  const selfAuthRoutes = ['/delete-account'];

  useEffect(() => {
    // Normalize pathname (strip trailing slash) for consistent matching
    const pathname = location.pathname.replace(/\/$/, '') || '/';
    // Only show login modal if not on a public route, not on a self-auth route, and user is not authenticated
    const isPublicRoute = publicRoutes.includes(pathname) || pathname.startsWith('/blog');
    const isSelfAuthRoute = selfAuthRoutes.includes(pathname);

    setShowLoginModal(!user && !isPublicRoute && !isSelfAuthRoute);
  }, [location.pathname, user]);

  const handleCloseLoginModal = () => {
    const pathname = location.pathname.replace(/\/$/, '') || '/';
    if (!user) {
      if (!publicRoutes.includes(pathname) && !pathname.startsWith('/blog') && !selfAuthRoutes.includes(pathname)) {
        return;
      }
    }
    setShowLoginModal(false);
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        Loading...
      </div>
    );
  }

  return (
    <div className="App">
      {/* Login Modal with auth success handler */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={handleCloseLoginModal}
        onAuthSuccess={onAuthSuccess}
      />

      <Routes>
        {/* Landing page at root — redirects to /app if authenticated */}
        <Route path="/" element={<LandingRedirect user={user} />} />

        {/* Authenticated app routes — wrapped in AppShell with sidebar nav */}
        <Route
          path="/app"
          element={
            <ThemeProvider>
              <CurrencyProvider>
                <SidebarProvider>
                  <FinanceDataProvider>
                    <AppShell />
                  </FinanceDataProvider>
                </SidebarProvider>
              </CurrencyProvider>
            </ThemeProvider>
          }
        >
          <Route index element={<CalendarScreen key={`calendar-${authTimestamp}`} />} />
          <Route path="budgets" element={<BudgetsPage />} />
          <Route path="accounts" element={<AccountsPage />} />
          <Route path="insights" element={<InsightsPage />} />
          <Route path="debts" element={<DebtPage />} />
          <Route path="savings" element={<SavingsPage />} />
          <Route path="ledger" element={<LedgerPage />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>

        {/* Legacy calendar route — redirect to /app */}
        <Route path="/calendar" element={<Navigate to="/app" replace />} />

        {/* Standalone authenticated routes (not in AppShell) */}
        <Route path="/metrics227603" element={<MetricsDashboard />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/success" element={<SuccessPage />} />

        {/* Public routes */}
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/blog" element={<BlogIndex />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/delete-account" element={<DeleteAccountPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />

        {/* Fallback redirect */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </div>
  );
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authTimestamp, setAuthTimestamp] = useState(null);
  const [visitTracked, setVisitTracked] = useState(false);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setUser(user);
      setLoading(false);

      // Set a timestamp when auth state changes (for forcing component refreshes)
      setAuthTimestamp(new Date().getTime());

      // Track visit for ALL visitors (cloud function handles anonymous writes)
      if (!visitTracked) {
        setVisitTracked(true);
        visitorTracker.trackVisit().catch(() => {});
        // User journey tracker still requires auth (writes directly to Firebase)
        if (user) {
          userJourneyTracker.initialize().then(() => {
            userJourneyTracker.trackVisit(userJourneyTracker._detectSource());
            userJourneyTracker.trackStep("App", "Visit");
          }).catch(() => {});
        }
      }
    });

    return () => unsubscribe();
  }, [visitTracked]);

  // Handle successful authentication
  const handleAuthSuccess = () => {
    // Update timestamp to force component remounts
    setAuthTimestamp(new Date().getTime());
  };

  return (
    <Router>
      <AppContent
        user={user}
        loading={loading}
        authTimestamp={authTimestamp}
        onAuthSuccess={handleAuthSuccess}
      />
    </Router>
  );
}

export default App;
